/**
 * Dots and Boxes Board Component
 * Renders the game board with dots, lines, and boxes
 */

import './DotsAndBoxesBoard.css';

const DotsAndBoxesBoard = ({
  gameState,
  onLineClick,
  disabled,
  currentUserId,
  hostUser,
  guestUser
}) => {
  if (!gameState) return null;

  const { rows, cols, horizontalLines, verticalLines, boxes } = gameState;

  // Safety check - ensure arrays exist
  if (!horizontalLines || !verticalLines || !boxes) {
    console.error('Game state missing required arrays:', { horizontalLines, verticalLines, boxes });
    return <div>Loading game board...</div>;
  }

  // Convert Firebase objects back to arrays if needed
  const ensureArray = (arr) => {
    if (Array.isArray(arr)) return arr;
    if (typeof arr === 'object' && arr !== null) {
      return Object.values(arr);
    }
    return [];
  };

  const safeHorizontalLines = ensureArray(horizontalLines).map(row => ensureArray(row));
  const safeVerticalLines = ensureArray(verticalLines).map(row => ensureArray(row));
  const safeBoxes = ensureArray(boxes).map(row => ensureArray(row));

  // Determine player colors
  const getPlayerColor = (playerId) => {
    if (!playerId) return null;
    return playerId === gameState.hostId ? '#2196F3' : '#FF9800';
  };

  // Handle horizontal line click
  const handleHorizontalClick = (row, col) => {
    if (disabled || (safeHorizontalLines[row] && safeHorizontalLines[row][col])) return;
    onLineClick('horizontal', row, col);
  };

  // Handle vertical line click
  const handleVerticalClick = (row, col) => {
    if (disabled || (safeVerticalLines[row] && safeVerticalLines[row][col])) return;
    onLineClick('vertical', row, col);
  };

  return (
    <div className="dotsandboxes-board">
      <div className="board-grid" style={{
        gridTemplateColumns: `repeat(${cols * 2 - 1}, auto)`,
        gridTemplateRows: `repeat(${rows * 2 - 1}, auto)`
      }}>
        {/* Render grid */}
        {Array(rows * 2 - 1).fill(null).map((_, gridRow) =>
          Array(cols * 2 - 1).fill(null).map((_, gridCol) => {
            const isDot = gridRow % 2 === 0 && gridCol % 2 === 0;
            const isHorizontalLine = gridRow % 2 === 0 && gridCol % 2 === 1;
            const isVerticalLine = gridRow % 2 === 1 && gridCol % 2 === 0;
            const isBox = gridRow % 2 === 1 && gridCol % 2 === 1;

            const key = `${gridRow}-${gridCol}`;

            // Render dot
            if (isDot) {
              return (
                <div key={key} className="dot" />
              );
            }

            // Render horizontal line
            if (isHorizontalLine) {
              const row = Math.floor(gridRow / 2);
              const col = Math.floor(gridCol / 2);
              const lineOwner = safeHorizontalLines[row] ? safeHorizontalLines[row][col] : '';
              const isDrawn = !!lineOwner;

              return (
                <div
                  key={key}
                  className={`line horizontal ${isDrawn ? 'drawn' : ''} ${disabled || isDrawn ? 'disabled' : ''}`}
                  onClick={() => handleHorizontalClick(row, col)}
                  style={{
                    backgroundColor: isDrawn ? getPlayerColor(lineOwner) : undefined
                  }}
                />
              );
            }

            // Render vertical line
            if (isVerticalLine) {
              const row = Math.floor(gridRow / 2);
              const col = Math.floor(gridCol / 2);
              const lineOwner = safeVerticalLines[row] ? safeVerticalLines[row][col] : '';
              const isDrawn = !!lineOwner;

              return (
                <div
                  key={key}
                  className={`line vertical ${isDrawn ? 'drawn' : ''} ${disabled || isDrawn ? 'disabled' : ''}`}
                  onClick={() => handleVerticalClick(row, col)}
                  style={{
                    backgroundColor: isDrawn ? getPlayerColor(lineOwner) : undefined
                  }}
                />
              );
            }

            // Render box
            if (isBox) {
              const boxRow = Math.floor(gridRow / 2);
              const boxCol = Math.floor(gridCol / 2);
              const boxOwner = safeBoxes[boxRow] ? safeBoxes[boxRow][boxCol] : '';
              const isClaimed = !!boxOwner;
              const ownerPhotoURL = isClaimed
                ? (boxOwner === gameState.hostId ? hostUser?.photoURL : guestUser?.photoURL)
                : null;

              return (
                <div
                  key={key}
                  className={`box ${isClaimed ? 'claimed' : ''}`}
                  style={{
                    backgroundColor: isClaimed ? getPlayerColor(boxOwner) : undefined
                  }}
                >
                  {isClaimed && ownerPhotoURL && (
                    <img
                      src={ownerPhotoURL}
                      alt="Player"
                      className="box-owner-avatar"
                    />
                  )}
                </div>
              );
            }

            return null;
          })
        )}
      </div>
    </div>
  );
};

export default DotsAndBoxesBoard;
